import React from "react";
import { cn } from "../../../lib/utils";
import { TocItem } from "../../../utils/docs/tocGenerator";

interface DocsTableOfContentsProps {
  toc: TocItem[];
  activeId: string | null;
  className?: string;
}

function scrollToHeading(id: string) {
  const element = document.getElementById(id);
  if (element) {
    const offset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
}

function TocItemComponent({
  item,
  activeId,
  level = 0,
}: {
  item: TocItem;
  activeId: string | null;
  level?: number;
}) {
  const isActive = item.id === activeId;
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li className={cn("mb-1", level > 0 && "ml-4")}>
      <button
        onClick={() => scrollToHeading(item.id)}
        className={cn(
          "text-left w-full px-2 py-1 text-sm transition-colors rounded-none",
          isActive
            ? "text-[var(--su-purple-accent)] font-medium bg-[var(--su-bg-secondary)]"
            : "text-[var(--su-text-muted)] hover:text-[var(--su-text-primary)] hover:bg-[var(--su-bg-secondary)]",
        )}
      >
        {item.text}
      </button>
      {hasChildren && (
        <ul className="mt-1">
          {item.children!.map((child) => (
            <TocItemComponent
              key={child.id}
              item={child}
              activeId={activeId}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function DocsTableOfContents({
  toc,
  activeId,
  className,
}: DocsTableOfContentsProps) {
  if (toc.length === 0) {
    return null;
  }

  return (
    <aside
      className={cn(
        "hidden lg:block w-64 flex-shrink-0 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto",
        className,
      )}
    >
      <div className="p-4">
        <h3 className="text-xs font-semibold text-[var(--su-text-muted)] uppercase tracking-wide mb-3">
          On this page
        </h3>
        <nav>
          <ul className="space-y-1">
            {toc.map((item) => (
              <TocItemComponent key={item.id} item={item} activeId={activeId} />
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
